// Approvals banner — surfaces kid chore completions waiting on a
// parent's approval. Lives at the top of the Chores tab whenever
// pendingApprovals.length > 0; collapsed by default to keep the
// chore chart's attention budget on the chart itself, expandable to
// show the full queue with per-row approve actions.
//
// Display intentionally low-shame: amber accent (matches the
// "Behind · whenever you can" rail in Today), no "X waiting on
// YOU" framing. Just "N approvals pending".

import { useState } from "preact/hooks";
import { Icon } from "../../icons";
import type { PendingApproval } from "../../lib/rpc";

interface ApprovalsBannerProps {
  pending: PendingApproval[];
  onApprove: (a: PendingApproval) => void;
  onDeny: (a: PendingApproval, reason: string | null) => void;
}

export function ApprovalsBanner({ pending, onApprove, onDeny }: ApprovalsBannerProps) {
  const [open, setOpen] = useState(false);
  // Which row (if any) is currently in "writing a deny reason" mode.
  // null when nobody is. Inline expansion below the row, not a modal —
  // saves a tap and keeps the surface calm.
  const [denyingId, setDenyingId] = useState<number | null>(null);
  const [reasonDraft, setReasonDraft] = useState("");

  const startDeny = (id: number) => {
    setDenyingId(id);
    setReasonDraft("");
  };
  const cancelDeny = () => {
    setDenyingId(null);
    setReasonDraft("");
  };
  const commitDeny = (p: PendingApproval) => {
    const reason = reasonDraft.trim();
    onDeny(p, reason || null);
    setDenyingId(null);
    setReasonDraft("");
  };

  return (
    <div className="chores-approvals">
      <button
        type="button"
        className="chores-approvals-summary"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <Icon name="star" size={14} />
        <span>
          {pending.length} kid {pending.length === 1 ? "completion" : "completions"} waiting for review
        </span>
        <span className="chores-approvals-caret">{open ? "▴" : "▾"}</span>
      </button>
      {open && (
        <ul className="chores-approvals-list">
          {pending.map((p) => (
            <li key={p.completionId} className="chores-approvals-row">
              <div className="chores-approvals-row-main">
                <span className="chores-approvals-icon">{p.choreIcon || "✅"}</span>
                <span className="chores-approvals-text">
                  <strong>{p.completedBy}</strong> · {p.choreName}
                </span>
                <span className="chores-approvals-stars">⭐ {p.choreStars}</span>
                {denyingId === p.completionId ? null : (
                  <>
                    <button
                      type="button"
                      className="chores-approvals-approve"
                      onClick={() => onApprove(p)}
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      className="chores-approvals-deny"
                      onClick={() => startDeny(p.completionId)}
                    >
                      Deny
                    </button>
                  </>
                )}
              </div>
              {denyingId === p.completionId && (
                <div className="chores-approvals-deny-form">
                  <input
                    type="text"
                    className="chores-approvals-deny-reason"
                    placeholder="Why? (optional)"
                    value={reasonDraft}
                    maxLength={200}
                    onInput={(e) => setReasonDraft((e.target as HTMLInputElement).value)}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") commitDeny(p);
                      else if (e.key === "Escape") cancelDeny();
                    }}
                  />
                  <button
                    type="button"
                    className="chores-approvals-deny-confirm"
                    onClick={() => commitDeny(p)}
                  >
                    Deny
                  </button>
                  <button
                    type="button"
                    className="chores-approvals-deny-cancel"
                    onClick={cancelDeny}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
